import { Skeleton } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type {
	MonitorActionSendEmail,
	NotificationRuleOut,
} from '@repo/api-codegen';
import {
	useGetNotificationRule,
	useUpdateNotificationRule,
} from '@repo/api-codegen';
import { FilterOptionType } from '@repo/common/components/Filter/types';
import { getFilterValueFromApiCatalogFilter } from '@repo/common/components/Filter/utils';
import { debounce } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useSlackChannels } from '../../api/hooks/workspace/useSlackChannels';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import {
	SearchFilterV2Store,
	SearchFilterV2StoreContext,
} from '../../components/Filter';
import { FILTER_OPTIONS_CONFIG } from '../../components/Filter/constants';
import { EMAIL_REGEX } from '../../constants';
import { NotificationRuleForm } from './components/NotificationRuleForm';

interface NotificationRuleFormControllerProps {
	ruleId: string;
	onClose: () => void;
}

export function NotificationRuleFormController({
	ruleId,
	onClose,
}: NotificationRuleFormControllerProps) {
	// Fetch the notification rule data
	const { data: rule, isLoading } = useGetNotificationRule({
		pathParams: { ruleId },
	});

	// Local state for immediate UI feedback
	const [localTitle, setLocalTitle] = useState('');
	const [localDescription, setLocalDescription] = useState('');

	// Filter store for managing monitor filters
	const [filterStore, setFilterStore] = useState<SearchFilterV2Store | null>(
		null
	);

	// Email form state
	const [isAddingEmail, setIsAddingEmail] = useState(false);
	const [newEmail, setNewEmail] = useState('');
	const [emailError, setEmailError] = useState('');

	// Slack form state
	const [isAddingSlackChannel, setIsAddingSlackChannel] = useState(false);

	// Initialize local state when rule data is loaded
	const [isLocalStateInitialized, setIsLocalStateInitialized] = useState(false);
	if (rule && !isLocalStateInitialized) {
		setLocalTitle(rule.title || '');
		setLocalDescription(rule.description || '');
		setIsLocalStateInitialized(true);
	}

	// Fetch Slack channels
	const { data: availableSlackChannels } = useSlackChannels({});

	const { mutateAsync: updateRule } = useUpdateNotificationRule();

	// Handle rule updates with type-safe key-value pairs
	const handleRuleUpdate = useCallback(
		async <K extends keyof NotificationRuleOut>(
			key: K,
			value: NotificationRuleOut[K]
		) => {
			if (!rule) return;

			try {
				await updateRule({
					pathParams: { ruleId: rule.id },
					body: {
						[key]: value,
					} as any,
				});
			} catch (error) {
				showNotification({
					title: 'Auto-save failed',
					message: 'Failed to save notification rule',
					color: 'red',
					autoClose: 3000,
				});
			}
		},
		[rule, updateRule]
	);

	// Debounced update function
	const debouncedUpdate = useMemo(
		() => debounce(handleRuleUpdate, 1000),
		[handleRuleUpdate]
	);

	// Handle local state updates with immediate UI feedback
	const handleLocalTitleUpdate = useCallback(
		(title: string) => {
			setLocalTitle(title);
			debouncedUpdate('title', title);
		},
		[debouncedUpdate]
	);

	const handleLocalDescriptionUpdate = useCallback(
		(description: string) => {
			setLocalDescription(description);
			debouncedUpdate('description', description);
		},
		[debouncedUpdate]
	);

	// Initialize filter store when rule is loaded
	const initializeFilterStore = useCallback(async () => {
		if (!rule || filterStore) return;

		const filterOptions = [
			FILTER_OPTIONS_CONFIG[FilterOptionType.TITLE],
			FILTER_OPTIONS_CONFIG[FilterOptionType.TAGS],
			FILTER_OPTIONS_CONFIG[FilterOptionType.DATABASE],
			FILTER_OPTIONS_CONFIG[FilterOptionType.SCHEMA],
			FILTER_OPTIONS_CONFIG[FilterOptionType.INTEGRATION],
		];

		let initialValues: any[] = [];

		// Load initial values from the filterConfig
		if (rule.filter_config && Object.keys(rule.filter_config).length > 0) {
			try {
				initialValues = await getFilterValueFromApiCatalogFilter(
					filterOptions,
					rule.filter_config
				);
			} catch (error) {
				// If conversion fails, start with empty values
				initialValues = [];
			}
		}

		const store = new SearchFilterV2Store({
			filterOptions,
			initialValues,
			// No persistence - no localStorage or URL sync
			preferencesLocalStorageKey: null,
			urlParamToSyncFilters: null,
			// Listen to filter changes and update the rule
			onFilterChanged: () => {
				// Use setTimeout to avoid updating state during render
				setTimeout(() => {
					if (store.catalogFilter) {
						debouncedUpdate('filter_config', store.catalogFilter);
					}
				}, 0);
			},
		});

		setFilterStore(store);
	}, [rule, filterStore, debouncedUpdate]);

	// Initialize filter store when rule is available
	if (rule && !filterStore) {
		initializeFilterStore();
	}

	// Email form management
	const handleSetIsAddingEmail = useCallback((isAdding: boolean) => {
		setIsAddingEmail(isAdding);
		if (!isAdding) {
			setNewEmail('');
			setEmailError('');
		}
	}, []);

	const handleSetNewEmail = useCallback((email: string) => {
		setNewEmail(email);
		setEmailError('');
	}, []);

	const handleAddEmail = useCallback(() => {
		if (!newEmail || !rule) return;

		if (!EMAIL_REGEX.test(newEmail)) {
			setEmailError('Invalid email');
			return;
		}

		const currentEmails =
			(rule.action_send_email as MonitorActionSendEmail)?.emails || [];

		if (currentEmails.includes(newEmail)) {
			setEmailError('Email already added');
			return;
		}

		const newEmailConfig = {
			emails: [...currentEmails, newEmail],
		};

		handleRuleUpdate('action_send_email', newEmailConfig);
		setIsAddingEmail(false);
		setNewEmail('');
		setEmailError('');
	}, [newEmail, rule, handleRuleUpdate]);

	const handleRemoveEmail = useCallback(
		(emailToRemove: string) => {
			if (!rule) return;

			const currentEmails =
				(rule.action_send_email as MonitorActionSendEmail)?.emails || [];
			const newEmailConfig = {
				emails: currentEmails.filter((email) => email !== emailToRemove),
			};

			handleRuleUpdate('action_send_email', newEmailConfig);
		},
		[rule, handleRuleUpdate]
	);

	if (isLoading || !rule) {
		return <Skeleton height={400} />;
	}

	const notificationRuleForm = (
		<NotificationRuleForm
			rule={rule}
			onRuleUpdate={debouncedUpdate}
			// Local state for immediate UI feedback
			localTitle={localTitle}
			localDescription={localDescription}
			onLocalTitleUpdate={handleLocalTitleUpdate}
			onLocalDescriptionUpdate={handleLocalDescriptionUpdate}
			filterStore={filterStore}
			// Email form props
			isAddingEmail={isAddingEmail}
			newEmail={newEmail}
			emailError={emailError}
			onSetIsAddingEmail={handleSetIsAddingEmail}
			onSetNewEmail={handleSetNewEmail}
			onAddEmail={handleAddEmail}
			onRemoveEmail={handleRemoveEmail}
			// Slack form props
			isAddingSlackChannel={isAddingSlackChannel}
			onSetIsAddingSlackChannel={setIsAddingSlackChannel}
			availableSlackChannels={availableSlackChannels}
			onClose={onClose}
		/>
	);

	return (
		<ErrorBoundary disableFallback>
			{filterStore ? (
				<SearchFilterV2StoreContext.Provider value={filterStore}>
					{notificationRuleForm}
				</SearchFilterV2StoreContext.Provider>
			) : (
				notificationRuleForm
			)}
		</ErrorBoundary>
	);
}
